import { EmailTemplate } from "screens/mass-mail/types";

/**
 * Candidate email templates in MassMail format.
 * Use {contact.firstName}, {contact.lastName} etc. as placeholders —
 * MassMail's placeholder engine replaces them per recipient automatically.
 *
 * Broker templates use {broker.fullName}, {broker.email}, {broker.phone}
 * which resolve from the candidate's unit brokerContact via contextPlaceholders.
 */

export const CANDIDATE_EMAIL_TEMPLATES: EmailTemplate[] = [
  {
    id: "rejection",
    label: "Afwijzing",
    templateType: "Kandidaten",
    description: "Na het versturen wordt de kandidatuur automatisch gearchiveerd. De kandidaat verdwijnt uit de actieve kandidatenlijst.",
    subject: "Betreft uw interesse in pand {unitId}",
    content: `<p><em style="color: #838C89;">English version below / Engelse versie hieronder</em></p>
<p>Beste {contact.fullName},</p>
<p>Bedankt voor uw interesse in ons pand <strong>{unitId}</strong>.</p>
<p>Helaas moeten we u meedelen dat er in de tussentijd al een huurder gekozen is voor dit pand.</p>
<p>We hopen dat u zich in de toekomst opnieuw kandidaat stelt voor één van onze andere panden.</p>
<p>U kunt onze beschikbare panden bekijken op onze website <a href="https://yally.apartments">https://yally.apartments</a>.</p>
<p>Met vriendelijke groeten,<br/>Het Yally team</p>
<hr/>
<p>Dear {contact.fullName},</p>
<p>Thank you for your interest in our property <strong>{unitId}</strong>.</p>
<p>Unfortunately, we must inform you that another tenant has been chosen for this property in the meantime.</p>
<p>We hope you will apply again in the future for one of our other properties.</p>
<p>You can view our available properties on our website <a href="https://yally.apartments">https://yally.apartments</a>.</p>
<p>Kind regards,<br/>The Yally team</p>`,
  },
  {
    id: "archive",
    label: "Archivering",
    templateType: "Kandidaten",
    description: "Na het versturen wordt de dubbele kandidatuur automatisch gearchiveerd. De kandidaat verdwijnt uit de actieve kandidatenlijst.",
    subject: "Betreft uw kandidatuur voor {unitId}",
    content: `<p><em style="color: #838C89;">English version below / Engelse versie hieronder</em></p>
<p>Beste {contact.fullName},</p>
<p>We hebben gemerkt dat u mogelijk dubbel voorkomt in onze kandidatenlijst voor pand <strong>{unitId}</strong>.</p>
<p>Om onze administratie up-to-date te houden, archiveren we deze kandidatuur.</p>
<p>Indien dit een fout is, neem dan gerust contact met ons op of stel je rechtstreeks kandidaat via <a href="https://yally.apartments/{unitId}">https://yally.apartments/{unitId}</a>.</p>
<p>Met vriendelijke groeten,<br/>Het Yally team</p>
<hr/>
<p>Dear {contact.fullName},</p>
<p>We noticed that you may appear twice in our candidate list for property <strong>{unitId}</strong>.</p>
<p>To keep our administration up-to-date, we are archiving this candidacy.</p>
<p>If this is an error, please feel free to contact us or apply directly via <a href="https://yally.apartments/{unitId}">https://yally.apartments/{unitId}</a>.</p>
<p>Kind regards,<br/>The Yally team</p>`,
  },
  {
    id: "invite",
    label: "Uitnodiging",
    templateType: "Kandidaten",
    description: "De kandidaat ontvangt een persoonlijke link om een bezichtiging in te plannen via yally.apartments. De status wordt bijgewerkt naar 'uitgenodigd'.",
    subject: "Dit pand wacht op jou… Plan snel je bezoek! {building.googlePlaceLongAddress}",
    content: `<p><em style="color: #838C89;">English version below / Engelse versie hieronder</em></p>
<p>Beste {contact.firstName},</p>
<p>Wat leuk dat je interesse toonde in het pand {building.googlePlaceLongAddress}.</p>
<p>De eerste indruk online is één ding… maar in het echt komt het pas écht tot leven. We nodigen je dan ook graag uit om het zelf te ontdekken.</p>
<p>Via deze link kan je eenvoudig een bezoekmoment inboeken op een moment dat voor jou past:</p>
<p><a href="https://yally.apartments/{unitId}?uuid=PLACEHOLDER_UUID">Plan je bezoek</a></p>
<p>We merken dat dit soort panden snel de deur uit zijn, dus wacht niet te lang met boeken 😉.</p>
<p>Heb je nog vragen? We staan voor je klaar!</p>
<p>Tot binnenkort?</p>
<p><strong>Ella</strong><br/>Team Yally</p>
<p><br/></p>
<hr/>
<p><br/></p>
<p><strong>English Version:</strong></p>
<p>Dear {contact.firstName},</p>
<p>How great that you showed interest in the property at {building.googlePlaceLongAddress}.</p>
<p>The first impression online is one thing… but in real life, it truly comes alive. That's why we'd love to invite you to come discover it for yourself.</p>
<p>Via this link, you can easily book a viewing at a time that suits you:</p>
<p><a href="https://yally.apartments/{unitId}?uuid=PLACEHOLDER_UUID">Book your visit</a></p>
<p>We've noticed that properties like this go fast, so don't wait too long to book 😉.</p>
<p>Any questions? We're here for you!</p>
<p>See you soon?</p>
<p><strong>Ella</strong><br/>Team Yally</p>`,
  },
  {
    id: "invite-reminder",
    label: "Herinnering uitnodiging",
    templateType: "Kandidaten",
    description: "Vriendelijke herinnering voor een kandidaat die nog geen bezichtiging heeft ingepland. Bevat dezelfde persoonlijke link als de uitnodiging.",
    subject: "Nog geen bezichtiging ingepland? {building.googlePlaceLongAddress}",
    content: `<p><em style="color: #838C89;">English version below / Engelse versie hieronder</em></p>
<p>Beste {contact.firstName},</p>
<p>Onlangs toonde je interesse in dit pand {building.googlePlaceLongAddress}.</p>
<p>We merkten dat je nog geen bezichtiging hebt ingepland. Misschien heb je nog geen moment gevonden dat goed uitkomt, of is het even uit je gedachten gegaan, dat kan natuurlijk gebeuren.</p>
<p>Daarom herinneren we je er graag nog even aan 😊.</p>
<p>Via onderstaande link kan je eenvoudig een moment kiezen dat voor jou past:</p>
<p><a href="https://yally.apartments/{unitId}?uuid=PLACEHOLDER_UUID">Plan je bezoek</a></p>
<p>We zijn ervan overtuigd dat dit pand het bekijken waard is en wie weet voelt het meteen als thuiskomen.</p>
<p>Hopelijk tot binnenkort!</p>
<p><strong>Ella</strong><br/>Team Yally</p>
<p><br/></p>
<hr/>
<p><br/></p>
<p><strong>English Version:</strong></p>
<p>Dear {contact.firstName},</p>
<p>You recently showed interest in this property at {building.googlePlaceLongAddress}.</p>
<p>We noticed you haven't scheduled a viewing yet. Maybe you haven't found a time that works for you, or it slipped your mind — that happens!</p>
<p>So we wanted to give you a gentle reminder 😊.</p>
<p>Via the link below, you can easily pick a moment that suits you:</p>
<p><a href="https://yally.apartments/{unitId}?uuid=PLACEHOLDER_UUID">Book your visit</a></p>
<p>We're convinced this property is worth seeing — and who knows, it might feel like coming home right away.</p>
<p>Hope to see you soon!</p>
<p><strong>Ella</strong><br/>Team Yally</p>`,
  },
  {
    id: "archive-noresponse",
    label: "Archivering - geen reactie",
    templateType: "Kandidaten",
    description: "Sluit de kandidatuur af nadat de kandidaat niet meer heeft gereageerd. Verwijst door naar het algemene aanbod op yally.apartments. De kandidatuur wordt automatisch gearchiveerd.",
    subject: "We sluiten je dossier voor {building.googlePlaceLongAddress} af… maar misschien vind je de perfecte match in een van onze andere panden?",
    content: `<p><em style="color: #838C89;">English version below / Engelse versie hieronder</em></p>
<p>Beste {contact.firstName},</p>
<p>We hebben je eerder gecontacteerd over dit pand {building.googlePlaceLongAddress}, maar hebben helaas niets meer van je gehoord.</p>
<p>Daarom sluiten we je kandidatuur hiervoor af.</p>
<p>Maar geen zorgen, dit betekent zeker niet het einde van je zoektocht. We hebben misschien wel andere panden die nog beter bij je passen.</p>
<p><a href="https://yally.apartments">Bekijk ons volledige aanbod</a></p>
<p>Wie weet zit jouw perfecte match daar wel tussen 💛</p>
<p>We helpen je graag verder wanneer je er klaar voor bent!</p>
<p><strong>Ella</strong><br/>Team Yally</p>
<p><br/></p>
<hr/>
<p><br/></p>
<p><strong>English Version:</strong></p>
<p>Dear {contact.firstName},</p>
<p>We reached out to you earlier about this property at {building.googlePlaceLongAddress}, but unfortunately haven't heard back from you.</p>
<p>For that reason, we're closing your application for this property.</p>
<p>But no worries — this certainly doesn't mean the end of your search. We may have other properties that suit you even better.</p>
<p><a href="https://yally.apartments">Browse our full listings</a></p>
<p>Who knows, your perfect match might be among them 💛</p>
<p>We're happy to help you further whenever you're ready!</p>
<p><strong>Ella</strong><br/>Team Yally</p>`,
  },
  {
    id: "broker-forward",
    label: "Doorsturen naar makelaar",
    templateType: "Kandidaten",
    description:
      "Stuurt de contactgegevens van deze kandidaat door naar de gekoppelde makelaar. De mail wordt naar de makelaar verstuurd (niet naar de kandidaat). De kandidatuur blijft actief.",
    // {candidate.*} placeholders hold the candidate details even when the
    // recipient is the broker (so {contact.*} = broker via recipient swap).
    subject: "Kandidaat voor pand {unitId} - {candidate.fullName}",
    content: `<p>Beste {broker.fullName},</p>
<p>Hierbij de contactgegevens van een kandidaat die interesse heeft in pand <strong>{unitId}</strong> — <em>{building.address}</em> — hetwelke wij aan u in verhuuropdracht hebben gegeven:</p>
<ul>
  <li><strong>Naam:</strong> {candidate.fullName}</li>
  <li><strong>E-mail:</strong> {candidate.email}</li>
  <li><strong>Telefoon:</strong> {candidate.phone}</li>
</ul>
<p><strong>Kan u dringend contact opnemen met deze kandidaat?</strong></p>
<p>Met vriendelijke groeten,<br/>Het Yally team</p>`,
  },
  {
    id: "broker-contact",
    label: "Verwijzen naar makelaar",
    templateType: "Kandidaten",
    description:
      "Laat de kandidaat weten dat hij rechtstreeks contact moet opnemen met de gekoppelde makelaar om een bezichtiging te plannen. De kandidatuur blijft actief.",
    subject: "Bezichtiging pand {unitId} - contacteer onze makelaar",
    content: `<p><em style="color: #838C89;">English version below / Engelse versie hieronder</em></p>
<p>Beste {contact.fullName},</p>
<p>We hebben jouw aanvraag om pand <strong>{unitId}</strong> — <em>{building.address}</em> — te bezichtigen goed ontvangen, bedankt voor je interesse!</p>
<p>De bezichtigingen voor dit pand worden verzorgd door onze makelaar(s). Je mag rechtstreeks contact opnemen met:</p>
<ul>{brokers.html}</ul>
<p>Zij plannen graag samen met jou een bezichtiging in.</p>
<p>Met vriendelijke groeten,<br/>Het Yally team</p>
<hr/>
<p>Dear {contact.fullName},</p>
<p>We have received your request to visit property <strong>{unitId}</strong> — <em>{building.address}</em> — thank you for your interest!</p>
<p>Viewings for this property are handled by our broker(s). Please contact them directly:</p>
<ul>{brokers.html}</ul>
<p>They will be happy to schedule a viewing with you.</p>
<p>Kind regards,<br/>The Yally team</p>`,
  },
];
